import classNames from "classnames";
import { Cell, CoreCellProps } from "./cell";
import { ReactNode, useMemo, useRef } from "react";
import styles from "./cell.module.scss";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../Tooltip";

export interface ListItemCellProps extends CoreCellProps {
  leftVisual?: ReactNode;
  rightVisual?: ReactNode;
  heading?: ReactNode;
  subheading?: ReactNode;
}

export const CellListItem = ({
  leftVisual,
  rightVisual,
  heading,
  subheading,
  className,
  showTooltip = true,
  ...rest
}: ListItemCellProps) => {
  const cellClassNames = classNames(className);

  const headingRef = useRef<HTMLDivElement>(null);
  const headingActualRef = useRef<HTMLDivElement>(null);
  const subheadingRef = useRef<HTMLDivElement>(null);
  const subheadingActualRef = useRef<HTMLDivElement>(null);

  const isHeadingOverflown =
    headingRef.current && headingActualRef.current
      ? headingRef.current.clientWidth > headingActualRef.current.clientWidth
      : false;

  const isSubheadingOverflown =
    subheadingRef.current && subheadingActualRef.current
      ? subheadingRef.current.clientWidth >
        subheadingActualRef.current.clientWidth
      : false;

  return (
    <div className={cellClassNames}>
      <Cell
        alignment="left"
        {...rest}
        className={styles.cellSupportingVisualContainer}
      >
        <div className={styles.cellSupportingVisualContent}>
          {leftVisual && (
            <div className={styles.cellVisualStart}>{leftVisual}</div>
          )}
          {(heading || subheading) && (
            <div className={styles.cellVisualText}>
              {heading && (
                <>
                  {/*
                    This heading does not get displayed!
                    This element will determine if the text width is greater than the container width.
                    Which will ultimately display a tooltip if so. 
                  */}
                  <div
                    ref={headingRef}
                    className={`${styles.cellVisualTitle} ${styles.tooltipTriggerText}`}
                    aria-hidden
                  >
                    {heading}
                  </div>

                  {!isHeadingOverflown || !showTooltip ? (
                    <div
                      ref={headingActualRef}
                      className={styles.cellVisualTitle}
                    >
                      {heading}
                    </div>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          ref={headingActualRef}
                          className={styles.cellVisualTitle}
                        >
                          {heading}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        variant="small"
                        align="center"
                        side="right"
                      >
                        {heading}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}
              {subheading && (
                <>
                  {/*
                    This subheading does not get displayed!
                    This element will determine if the text width is greater than the container width.
                    Which will ultimately display a tooltip if so. 
                  */}
                  <div
                    ref={subheadingRef}
                    className={`${styles.cellVisualDescription} ${styles.tooltipTriggerText}`}
                    aria-hidden
                  >
                    {subheading}
                  </div>

                  {!isSubheadingOverflown || !showTooltip ? (
                    <div
                      ref={subheadingActualRef}
                      className={styles.cellVisualDescription}
                    >
                      {subheading}
                    </div>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          ref={subheadingActualRef}
                          className={styles.cellVisualDescription}
                        >
                          {subheading}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        variant="small"
                        align="center"
                        side="right"
                      >
                        {subheading}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {rightVisual && (
          <div className={styles.cellVisualEnd}>{rightVisual}</div>
        )}
      </Cell>
    </div>
  );
};
